import { CssProperty, CssValue, CssVariable } from './types';

/**
 * Maps CSS properties to their corresponding design system token values.
 *
 * @template TokenShape The type of token value (e.g., ColorName, SizeName)
 * @example
 * ```typescript
 * // Color token map
 * const colorTokens: TokenMap<ColorName> = {
 *   'color': 'functional-default',
 *   'background-color': 'base-dark'
 * };
 *
 * // Size token map
 * const sizeTokens: TokenMap<SizeName> = {
 *   'width': 'lg',
 *   'height': 'md'
 * };
 * ```
 */
export type TokenMap<TokenShape> = Record<CssProperty, TokenShape>;

/**
 * Function that converts a design system token to a CSS variable.
 *
 * @template Token The type of token value (e.g., ColorName, SizeName)
 * @param token The token value to convert
 * @param property The CSS property this token is being applied to
 * @returns A CSS variable string (e.g., 'var(--color-functional-default)')
 *
 * @example
 * ```typescript
 * const colorMapper: TokenMapper<ColorName> = (token, property) =>
 *   property.startsWith('background') ? `var(--bg-${token})` : `var(--color-${token})`;
 * ```
 */
export type TokenMapper<Token> = (token: Token, property: CssProperty) => CssValue;

/**
 * Maps CSS properties to their final CSS variable values.
 *
 * @example
 * ```typescript
 * const styles: VariableMap = {
 *   'color': 'var(--color-functional-default)',
 *   'background-color': 'var(--color-base-dark)'
 * };
 * ```
 */
export type VariableMap = Record<CssProperty, CssValue>;

/**
 * Transforms a map of CSS properties to tokens into a map of CSS properties to variables.
 *
 * This function takes a mapping of CSS properties to design system tokens and a token mapper function,
 * and returns a new mapping where each token has been converted to its corresponding CSS variable.
 *
 * @template TokenShape The type of token value (e.g., ColorName, SizeName)
 * @param cssToTokenMap Mapping of CSS properties to their token values
 * @param tokenToVariable Function that converts a token to a CSS variable
 * @returns Mapping of CSS properties to their CSS variable values
 *
 * @example
 * ```typescript
 * const colorTokens: TokenMap<ColorName> = {
 *   'color': 'functional-default',
 *   'background-color': 'base-dark'
 * };
 *
 * const variables = mapTokensToVariables(
 *   colorTokens,
 *   (token) => `var(--color-${token})`
 * );
 * // Result:
 * // {
 * //   'color': 'var(--color-functional-default)',
 * //   'background-color': 'var(--color-base-dark)'
 * // }
 * ```
 */
export function mapTokensToVariables<TokenShape>(
    cssToTokenMap: TokenMap<TokenShape>,
    tokenToVariable: TokenMapper<TokenShape>
): VariableMap {
    return Object.entries(cssToTokenMap).reduce<VariableMap>(
        (acc, [cssProperty, token]) => ({
            ...acc,
            [cssProperty]: tokenToVariable(token, cssProperty as CssProperty)
        }),
        {}
    );
}
